import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Stack,
    Typography,
    Box,
    Chip,
    Link,
    Divider,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Collapse,
    ButtonBase,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { API_URL } from '../api';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const fmtGBP = (n) => `£${Number(n || 0).toFixed(2)}`;
const statusChipColor = (s) => ({
    paid: 'success',
    sent: 'primary',
    overdue: 'error',
    draft: 'default',
    void: 'default',
}[s] || 'default');
const attendanceChipColor = (s) => ({ present: 'success', absent: 'error', late: 'warning' }[s] || 'default');
const attendanceLabel = (s) => ({ present: 'P', absent: 'A', late: 'L' }[s] || '—');
const reportStatusColor = (s) => ({ sent: 'success', draft: 'default' }[s] || 'default');

/**
 * Renders a stack of "everything about this account" cards:
 *   - Enrolled classes (live tutoring classes with the tutor + student count)
 *   - Recent progress reports
 *   - Invoices (with status counts)
 *   - Recent attendance
 *
 * Fetches from GET /api/admin/accounts/:id/rollup. Any section can be
 * empty; it renders a soft "None yet" placeholder rather than a blank
 * card.
 */
const AccountRollupCards = ({ account }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!account?._id) return;
        setLoading(true);
        setError('');
        axios
            .get(`${API_URL}/api/admin/accounts/${account._id}/rollup`, authHeaders())
            .then((res) => setData(res.data))
            .catch((err) => setError(err?.response?.data?.message || 'Failed to load related data.'))
            .finally(() => setLoading(false));
    }, [account?._id]);

    if (loading) {
        return (
            <Card sx={{ p: 3, mb: 3 }}>
                <Stack alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                </Stack>
            </Card>
        );
    }
    if (error) {
        return (
            <Card sx={{ p: 3, mb: 3 }}>
                <Typography color="error" variant="body2">{error}</Typography>
            </Card>
        );
    }
    if (!data) return null;

    return (
        <>
            <ClassesCard classes={data.currentClasses || []} />
            {account.role !== 'teacher' && (
                <AttendanceCard rows={data.attendance || []} />
            )}
            <ReportsCard rows={data.recentReports || []} />
            {(data.invoices?.latest?.length || Object.keys(data.invoices?.statuses || {}).length > 0) && (
                <InvoicesCard invoices={data.invoices} />
            )}
        </>
    );
};

const ClassesCard = ({ classes }) => (
    <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Enrolled classes</Typography>
            <Typography variant="caption" color="text.secondary">
                {classes.length} live class{classes.length === 1 ? '' : 'es'}
            </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Every currently-published tutoring class this account is on the roster of (or, for tutors,
            teaching). Class engagement is auto-synced from this list — see the Engagement chip above.
        </Typography>
        {classes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Not on any live class right now.</Typography>
        ) : (
            <Stack spacing={1}>
                {classes.map((c) => (
                    <Box
                        key={c._id}
                        component={RouterLink}
                        to={`/tutoring-classes/${c._id}`}
                        sx={{
                            display: 'block',
                            textDecoration: 'none',
                            color: 'inherit',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            '&:hover': { borderColor: 'primary.main' },
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {WEEKDAYS[c.dayOfWeek]} · {c.startsAt} · {c.yearGroup || '—'} · {c.subject || '—'}
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary">
                            Tutor: {c.tutorId ? `${c.tutorId.firstName} ${c.tutorId.surname}` : '—'}
                            {' '}· Roster: {(c.studentIds || []).length} student{(c.studentIds || []).length === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        )}
    </Card>
);

const ReportsCard = ({ rows }) => (
    <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Recent progress reports</Typography>
            <Typography variant="caption" color="text.secondary">{rows.length} shown</Typography>
        </Stack>
        {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No reports written yet.</Typography>
        ) : (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Tutor</TableCell>
                        <TableCell>Updated</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r._id}>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={r.status}
                                    color={reportStatusColor(r.status)}
                                    variant={r.status === 'draft' ? 'outlined' : 'filled'}
                                />
                            </TableCell>
                            <TableCell>
                                {r.studentId ? `${r.studentId.firstName} ${r.studentId.surname}` : '—'}
                            </TableCell>
                            <TableCell>
                                {r.tutoringClassId?._id ? (
                                    <Link component={RouterLink} to={`/tutoring-classes/${r.tutoringClassId._id}`}>
                                        {r.tutoringClassId.name}
                                    </Link>
                                ) : '—'}
                            </TableCell>
                            <TableCell>
                                {r.tutorId ? `${r.tutorId.firstName} ${r.tutorId.surname}` : '—'}
                            </TableCell>
                            <TableCell>{fmtDate(r.sentAt || r.submittedAt || r.updatedAt)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        )}
    </Card>
);

const InvoicesCard = ({ invoices }) => (
    <Card sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Invoices</Typography>
            <Stack direction="row" spacing={0.5}>
                {Object.entries(invoices.statuses || {}).map(([status, n]) => (
                    <Chip
                        key={status}
                        size="small"
                        label={`${n} ${status}`}
                        color={statusChipColor(status)}
                        variant={status === 'draft' ? 'outlined' : 'filled'}
                    />
                ))}
            </Stack>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {invoices.latest?.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No invoices raised yet.</Typography>
        ) : (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Due</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {invoices.latest.map((inv) => (
                        <TableRow key={inv._id}>
                            <TableCell>
                                <Link component={RouterLink} to={`/invoices/${inv._id}`}>
                                    {inv.paymentReference || '—'}
                                </Link>
                            </TableCell>
                            <TableCell>{inv.invoiceNumber}</TableCell>
                            <TableCell>{fmtGBP(inv.total)}</TableCell>
                            <TableCell>{fmtDate(inv.dueDate)}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={inv.status}
                                    color={statusChipColor(inv.status)}
                                    variant={inv.status === 'draft' ? 'outlined' : 'filled'}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        )}
    </Card>
);

const AttendanceCard = ({ rows }) => {
    const [open, setOpen] = useState(false);
    return (
        <Card sx={{ p: 3, mb: 3 }}>
            <ButtonBase
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.25,
                    mb: open ? 1 : 0,
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ExpandMoreRoundedIcon
                        sx={{
                            color: 'text.secondary',
                            transition: 'transform 200ms',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                    <Typography variant="h6">Recent attendance</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {rows.length} lesson{rows.length === 1 ? '' : 's'}
                </Typography>
            </ButtonBase>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No attendance recorded yet.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Student</TableCell>
                                <TableCell align="center">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((a) => (
                                <TableRow key={a._id}>
                                    <TableCell>{fmtDate(a.lessonDate)}{a.startsAt ? ` · ${a.startsAt}` : ''}</TableCell>
                                    <TableCell>{a.className || '—'}</TableCell>
                                    <TableCell>{a.studentName || '—'}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={attendanceLabel(a.status)}
                                            color={attendanceChipColor(a.status)}
                                            sx={{ minWidth: 38 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Collapse>
        </Card>
    );
};

export default AccountRollupCards;
